import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Download, FileText, Mail, ScrollText, Trash2, ListChecks, Send, Users, Lock, Unlock, ClipboardList, ShieldCheck } from "lucide-react";
import {
  getResolution,
  getCompany,
  deleteResolution,
  lockResolution,
  unlockResolution,
  downloadResolutionDocxUrl,
  downloadResolutionPdfUrl,
} from "@/lib/api";
import { toast } from "sonner";
import EmailModal from "@/components/EmailModal";
import DscSignModal from "@/components/DscSignModal";
import ResolutionAuditLog from "@/components/ResolutionAuditLog";
import { formatIndianDate } from "@/lib/date";
import { useAuth } from "@/contexts/AuthContext";

const TABS = [
  { key: "resolution", label: "Board Resolution", icon: ScrollText },
  { key: "shareholder_resolution", label: "Shareholder Resolution", icon: Users },
  { key: "notice", label: "Notice", icon: Mail },
  { key: "minutes", label: "Minutes", icon: FileText },
  { key: "procedure", label: "Procedure", icon: ListChecks },
];

export default function ResolutionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { role } = useAuth();
  const isAdmin = role === "admin";
  const [resolution, setResolution] = useState(null);
  const [company, setCompany] = useState(null);
  const [tab, setTab] = useState("resolution");
  const [emailOpen, setEmailOpen] = useState(false);
  const [dscOpen, setDscOpen] = useState(false);
  const [lockBusy, setLockBusy] = useState(false);
  const [auditTick, setAuditTick] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const r = await getResolution(id);
        setResolution(r.data);
        const c = await getCompany(r.data.company_id);
        setCompany(c.data);
      } catch {
        toast.error("Failed to load resolution");
      }
    })();
  }, [id]);

  if (!resolution) return <div className="max-w-5xl mx-auto p-10 text-zinc-500">Loading…</div>;

  const isLocked = Boolean(resolution.is_locked);

  const body =
    tab === "notice" ? resolution.notice_body :
    tab === "minutes" ? resolution.minutes_body :
    tab === "shareholder_resolution" ? resolution.shareholder_resolution_body :
    tab === "procedure" ? "" :
    resolution.resolution_body;

  const noContentForTab =
    (tab === "notice" && !resolution.notice_body) ||
    (tab === "minutes" && !resolution.minutes_body) ||
    (tab === "shareholder_resolution" && !resolution.shareholder_resolution_body);

  const handleDelete = async () => {
    if (isLocked) {
      toast.error("This resolution is locked. Unlock it first.");
      return;
    }
    if (!window.confirm("Delete this resolution?")) return;
    try {
      await deleteResolution(id);
      toast.success("Deleted");
      navigate("/resolutions");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  };

  const handleLock = async () => {
    const note = window.prompt(
      "Lock this resolution as Signed & Final?\n\nOptional note (e.g. 'Signed by all directors on 30 Sep 2024'):",
      "",
    );
    if (note === null) return;
    setLockBusy(true);
    try {
      const r = await lockResolution(id, note);
      setResolution(r.data);
      setAuditTick((t) => t + 1);
      toast.success("Resolution locked as Signed & Final");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not lock resolution");
    } finally {
      setLockBusy(false);
    }
  };

  const handleUnlock = async () => {
    if (!window.confirm("Unlock this resolution? Members will be able to edit and delete it again.")) return;
    setLockBusy(true);
    try {
      const r = await unlockResolution(id);
      setResolution(r.data);
      setAuditTick((t) => t + 1);
      toast.success("Resolution unlocked");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not unlock resolution");
    } finally {
      setLockBusy(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10" data-testid="resolution-detail-page">
      <button onClick={() => navigate(-1)} className="text-sm text-zinc-500 hover:text-[#002FA7] mb-6 flex items-center gap-2" data-testid="back-button">
        <ArrowLeft size={14} /> Back
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            <span className="font-mono-plex text-xs text-zinc-500" data-testid="resolution-number">{resolution.resolution_number}</span>
            {resolution.is_special_resolution && (
              <span className="text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 bg-[#002FA7] text-white">Special Resolution</span>
            )}
            <span className="text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 border border-zinc-300 text-zinc-700">FY {resolution.financial_year}</span>
            {isLocked && (
              <span
                className="text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 bg-amber-50 border border-amber-300 text-amber-800 flex items-center gap-1.5"
                data-testid="locked-badge"
                title={resolution.locked_note || ""}
              >
                <Lock size={10} /> Signed &amp; Final
              </span>
            )}
          </div>
          <h1 className="font-heading text-3xl font-light tracking-tight" data-testid="resolution-title">{resolution.resolution_title}</h1>
          <p className="text-sm text-zinc-500 mt-1">{resolution.company_name} · Meeting on {formatIndianDate(resolution.meeting_date)}</p>
          {isLocked && resolution.locked_by_name && (
            <p className="text-[11px] text-amber-800 mt-1.5" data-testid="lock-meta">
              Locked by <span className="font-medium">{resolution.locked_by_name}</span>
              {resolution.locked_at ? ` on ${formatIndianDate(resolution.locked_at)}` : ""}
              {resolution.locked_note ? ` — “${resolution.locked_note}”` : ""}
            </p>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          {tab !== "procedure" && (
            <>
              <a href={downloadResolutionPdfUrl(id, tab)} className="swiss-btn-primary text-sm" data-testid={`download-pdf-${tab}`} target="_blank" rel="noreferrer">
                <Download size={14} /> PDF
              </a>
              <a href={downloadResolutionDocxUrl(id, tab)} className="swiss-btn-ghost text-sm" data-testid={`download-docx-${tab}`} target="_blank" rel="noreferrer">
                <Download size={14} /> Word
              </a>
            </>
          )}
          <button onClick={() => setEmailOpen(true)} className="swiss-btn-ghost text-sm" data-testid="open-email-modal">
            <Send size={14} /> Email
          </button>
          <button
            onClick={() => setDscOpen(true)}
            className="swiss-btn-ghost text-sm"
            data-testid="open-dsc-modal"
            title="Cryptographically sign this Board Resolution PDF using your DSC USB token"
          >
            <ShieldCheck size={14} /> Sign with DSC
          </button>
          {(resolution.meeting_type === "agm" || resolution.meeting_type === "egm") && (
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/resolutions/${id}/proxy-register`}
              className="swiss-btn-ghost text-sm"
              data-testid="download-proxy-register"
              target="_blank"
              rel="noreferrer"
              title="Proxy Register: summary table + one pre-filled MGT-11 per member"
            >
              <ClipboardList size={14} /> Proxy Register
            </a>
          )}
          {isAdmin && !isLocked && (
            <button
              onClick={handleLock}
              disabled={lockBusy}
              className="text-sm border border-amber-300 text-amber-800 px-3 py-1.5 hover:bg-amber-50 transition-colors flex items-center gap-2 disabled:opacity-50"
              data-testid="lock-resolution-button"
              title="Lock as Signed & Final — blocks edits and deletes by all roles"
            >
              <Lock size={14} /> Lock as Final
            </button>
          )}
          {isAdmin && isLocked && (
            <button
              onClick={handleUnlock}
              disabled={lockBusy}
              className="text-sm border border-zinc-300 text-zinc-700 px-3 py-1.5 hover:bg-zinc-50 transition-colors flex items-center gap-2 disabled:opacity-50"
              data-testid="unlock-resolution-button"
            >
              <Unlock size={14} /> Unlock
            </button>
          )}
          {!isLocked && (
            <button
              onClick={handleDelete}
              className="text-zinc-400 hover:text-red-600 p-2"
              data-testid="delete-resolution"
              aria-label="Delete"
            >
              <Trash2 size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-zinc-200 mb-6" data-testid="doc-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          const disabled =
            (t.key === "notice" && !resolution.notice_body) ||
            (t.key === "minutes" && !resolution.minutes_body) ||
            (t.key === "shareholder_resolution" && !resolution.shareholder_resolution_body) ||
            (t.key === "procedure" && !(resolution.procedure_followed || []).length);
          return (
            <button
              key={t.key}
              onClick={() => !disabled && setTab(t.key)}
              disabled={disabled}
              className={`px-5 py-3 text-sm font-medium border-b-2 flex items-center gap-2 transition-colors ${
                active ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-zinc-600 hover:text-[#002FA7]"
              } ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
              data-testid={`tab-${t.key}`}
            >
              <Icon size={14} />
              {t.label}
              {disabled && <span className="text-[10px] text-zinc-400">(not generated)</span>}
            </button>
          );
        })}
      </div>

      {/* Document preview — A4 style */}
      <div className="bg-zinc-100 p-6 md:p-10" data-testid="doc-preview-wrap">
        <div className="a4-page max-w-4xl mx-auto" data-testid="doc-preview">
          {tab === "procedure" ? (
            <ProcedureView resolution={resolution} />
          ) : (
            <>
              {/* Letterhead */}
              <div className="text-center pb-4 border-b border-zinc-300">
                <div className="font-heading font-bold text-2xl tracking-tight text-[#002FA7]">{(company?.name || resolution.company_name).toUpperCase()}</div>
                {company?.cin && <div className="text-xs text-zinc-600 mt-1 font-mono-plex">CIN: {company.cin}</div>}
                {company?.registered_address && <div className="text-xs text-zinc-600 mt-1">{company.registered_address}</div>}
                {company?.email && <div className="text-xs text-zinc-600">Email: {company.email}</div>}
              </div>

              <h2 className="text-center font-heading font-bold text-lg mt-6 mb-4 tracking-wide">
                {tab === "notice"
                  ? (resolution.meeting_type === "agm" ? "NOTICE OF ANNUAL GENERAL MEETING"
                     : resolution.meeting_type === "egm" ? "NOTICE OF EXTRA-ORDINARY GENERAL MEETING"
                     : "NOTICE OF BOARD MEETING")
                  : tab === "minutes"
                    ? (resolution.meeting_type === "agm" ? "MINUTES OF THE ANNUAL GENERAL MEETING"
                       : resolution.meeting_type === "egm" ? "MINUTES OF THE EXTRA-ORDINARY GENERAL MEETING"
                       : "MINUTES OF THE BOARD MEETING")
                    : tab === "shareholder_resolution"
                      ? (resolution.is_special_resolution ? "SHAREHOLDER SPECIAL RESOLUTION" : "SHAREHOLDER ORDINARY RESOLUTION")
                      : "BOARD RESOLUTION"}
              </h2>

              {/* Meta */}
              <div className="text-sm mb-6">
                {tab === "notice" ? (
                  <>
                    <div className="tabular-row"><span>Notice Date</span><span>{resolution.notice_date || "—"}</span></div>
                    <div className="tabular-row"><span>Meeting Date</span><span>{formatIndianDate(resolution.meeting_date)}</span></div>
                    <div className="tabular-row"><span>Meeting Time</span><span>{resolution.meeting_time}</span></div>
                    <div className="tabular-row"><span>Venue</span><span>{resolution.meeting_place}</span></div>
                  </>
                ) : tab === "minutes" ? (
                  <>
                    <div className="tabular-row"><span>Resolution No.</span><span className="font-mono-plex">{resolution.resolution_number}</span></div>
                    <div className="tabular-row"><span>Meeting Date</span><span>{formatIndianDate(resolution.meeting_date)}</span></div>
                    <div className="tabular-row"><span>Meeting Time</span><span>{resolution.meeting_time}</span></div>
                    <div className="tabular-row"><span>Venue</span><span>{resolution.meeting_place}</span></div>
                  </>
                ) : (
                  <>
                    <div className="tabular-row"><span>Resolution No.</span><span className="font-mono-plex">{resolution.resolution_number}</span></div>
                    <div className="tabular-row"><span>Date of Meeting</span><span>{formatIndianDate(resolution.meeting_date)}</span></div>
                  </>
                )}
              </div>

              {tab === "resolution" && (
                <div className="font-medium mb-4">{resolution.resolution_title}</div>
              )}

              {noContentForTab ? (
                <div className="text-center text-zinc-500 py-10 italic text-sm">This document was not generated. Regenerate the resolution and tick the corresponding option.</div>
              ) : (
                <div className="whitespace-pre-wrap leading-relaxed text-[15px]" data-testid="doc-body">{body}</div>
              )}

              {/* Signature block */}
              {tab !== "notice" && !noContentForTab && (
                <div className="mt-12 pt-8 border-t border-zinc-300">
                  <p className="mb-2">For and on behalf of the Board of Directors</p>
                  <p className="font-medium mb-10">{(company?.name || resolution.company_name).toUpperCase()}</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                    {(resolution.signing_directors || []).map((d, i) => (
                      <div key={d.din || d.name || `signer-${i}`} className="border-t border-zinc-400 pt-2">
                        <div className="font-medium">{d.name}</div>
                        <div className="text-xs text-zinc-600 font-mono-plex">DIN: {d.din || "—"}</div>
                        <div className="text-xs text-zinc-600">{d.designation}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <EmailModal open={emailOpen} onClose={() => setEmailOpen(false)} resolution={resolution} company={company} />
      <DscSignModal
        open={dscOpen}
        onClose={() => setDscOpen(false)}
        resolution={resolution}
        company={company}
        onSigned={() => setAuditTick((t) => t + 1)}
      />
      <ResolutionAuditLog resolutionId={id} refreshKey={auditTick} />
    </div>
  );
}

function ProcedureView({ resolution }) {
  const steps = resolution.procedure_followed || [];
  const sections = resolution.applicable_sections || [];
  const forms = resolution.roc_forms || [];
  return (
    <div data-testid="procedure-view">
      <h2 className="text-center font-heading font-bold text-lg mb-2 tracking-wide">PROCEDURE & STATUTORY COMPLIANCE</h2>
      <p className="text-center text-xs text-zinc-500 mb-6">Companies Act, 2013 · Step-by-step checklist for: {resolution.resolution_title}</p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <InfoBlock label="Applicable Section / Rule" value={sections.length ? sections.join(" · ") : "—"} />
        <InfoBlock label="ROC e-Form(s)" value={forms.length ? forms.join(" · ") : "—"} />
        <InfoBlock label="Statutory Timeline" value={resolution.statutory_timeline || "—"} />
      </div>

      {steps.length === 0 ? (
        <p className="text-zinc-500 text-sm">No procedure steps recorded for this resolution type.</p>
      ) : (
        <ol className="space-y-3 list-decimal pl-6 text-[15px] leading-relaxed">
          {steps.map((s, i) => (
            // Steps are procedural text strings without ids — combine the (stable) text content
            // with the index so React still has a unique key but reorder-resilient when steps differ.
            <li key={`step-${i}-${(s || "").slice(0, 40)}`} data-testid={`procedure-step-${i}`}>{s}</li>
          ))}
        </ol>
      )}

      <div className="mt-10 p-4 border border-zinc-300 bg-zinc-50 text-xs text-zinc-600 leading-relaxed">
        <strong className="text-zinc-800">Disclaimer:</strong> This procedural checklist is a guide based on the
        Companies Act, 2013 and Secretarial Standards. Always verify the latest provisions and consult a qualified
        Company Secretary / professional before filing.
      </div>
    </div>
  );
}

function InfoBlock({ label, value }) {
  return (
    <div className="border border-zinc-200 p-3">
      <div className="label-eyebrow mb-1">{label}</div>
      <div className="text-sm">{value}</div>
    </div>
  );
}
